import { customElement, property, query } from 'lit/decorators.js';
import { TailwindElement } from '../../shared/tailwind.element';
import { type TemplateResult, html } from 'lit';
import { textItems } from '../../constants/text';
import { CustomEventNames } from '../../constants/custom-event-names';
import { type Ref, createRef, ref } from 'lit/directives/ref.js';
import { apiService } from '../../services/apiService';

@customElement('szw-ba-enter-code-dialog')
export class SzwBaEnterCodeDialog extends TailwindElement() {
	@query('dialog')
	dialog!: HTMLDialogElement;

	@property({ attribute: false }) errorMessage: string = '';

	private readonly codeInputRef: Ref<HTMLInputElement> = createRef();
	private readonly boundHandleClose: (event: Event) => void;

	constructor() {
		super();
		this.boundHandleClose = this.handleClose.bind(this);
	}

	connectedCallback(): void {
		super.connectedCallback();
		window.addEventListener(CustomEventNames.ShowEnterCodeDialog, this.handleEnterCode.bind(this) as EventListener);
	}

	disconnectedCallback(): void {
		super.disconnectedCallback();
		window.removeEventListener(CustomEventNames.ShowEnterCodeDialog, this.handleEnterCode.bind(this) as EventListener);
	}

	handleEnterCode(): void {
		this.dialog.showModal();
	}

	async openQuestionaire(event: SubmitEvent): Promise<void> {
		event.preventDefault();
		const form = event.target as HTMLFormElement;
		this.errorMessage = '';
		if (form.checkValidity()) {
			const formData = new FormData(form);
			const responseCode = formData.get('responseCode') as string;
			const openQuestionaireResult = await apiService.openQuestionnaire(responseCode);
			if (openQuestionaireResult != null) {
				localStorage.setItem('responseCode', responseCode);
				window.location.href = '/questionnaire?autoresume=true';
			}
		} else {
			if (this.codeInputRef.value?.validity.valueMissing ?? false) {
				this.errorMessage = textItems.getQuestionaireByCodeErrorCodeRequired;
			} else if (
				(this.codeInputRef.value?.validity.tooShort ?? false) ||
				(this.codeInputRef.value?.validity.tooLong ?? false)
			) {
				this.errorMessage = textItems.getQuestionaireByCodeErrorInvalidLength;
			} else {
				this.errorMessage = textItems.getQuestionaireByCodeErrorInvalid;
			}
		}
	}

	render(): TemplateResult {
		return html`
			<dialog aria-modal="true" aria-labelledby="dialogheader" class="w-full max-w-xl">
				<section class="flex flex-col gap-6 bg-white p-6">
					<header class="flex justify-between gap-2">
						<h2 id="dialogheader">${textItems.getQuestionaireByCode}</h2>
						<button @click=${this.boundHandleClose} aria-label="${textItems.close}">
							<svg
								width="24"
								height="24"
								viewBox="0 0 24 24"
								class="shrink-0"
								fill="none"
								xmlns="http://www.w3.org/2000/svg"
								title="${textItems.icons.close}"
							>
								<g>
									<path
										d="M17.6871 4.21371L12.0103 9.24151L6.33349 4.21361C5.73649 3.68481 4.83499 3.69821 4.25439 4.24411C3.70839 4.82461 3.69499 5.72621 4.22389 6.32311L9.25179 12L4.22389 17.6769C3.69499 18.2738 3.70839 19.1754 4.25429 19.7559C4.83489 20.3018 5.73639 20.3152 6.33339 19.7863L12.0102 14.7584L17.687 19.7863C18.2839 20.3152 19.1855 20.3018 19.7661 19.756C20.312 19.1754 20.3254 18.2739 19.7965 17.6769L14.7687 12L19.7966 6.32321C20.3255 5.72621 20.312 4.82471 19.7662 4.24411C19.1855 3.69821 18.284 3.68481 17.6871 4.21371ZM12.0103 12.0145L12.003 12.0072L11.9958 12L12.0031 11.9927L12.0103 11.9855L12.0176 11.9928L12.0248 12L12.0176 12.0072L12.0103 12.0145Z"
										fill="#696969"
									/>
								</g>
							</svg>
						</button>
					</header>
					<p id="responseCodeLabel">${textItems.getQuestionaireByCodeClarification}</p>
					<form
						@submit=${this.openQuestionaire}
						autocomplete="off"
						class="flex flex-col gap-6"
						novalidate
						aria-autocomplete="none"
					>
						${this.errorMessage.length > 0
							? html` <p class="error-message flex w-full gap-4 bg-rood-15 p-4" id="error-message" role="alert">
									<svg
										width="22"
										height="22"
										viewBox="0 0 22 22"
										class="shrink-0"
										fill="none"
										xmlns="http://www.w3.org/2000/svg"
										title="Waarschuwing icoon"
									>
										<g>
											<g>
												<g>
													<path
														d="M11 21.5C16.799 21.5 21.5 16.799 21.5 11C21.5 5.20101 16.799 0.5 11 0.5C5.20101 0.5 0.5 5.20101 0.5 11C0.5 16.799 5.20101 21.5 11 21.5Z"
														fill="#D52B1E"
													></path>
												</g>
												<path
													d="M14.1192 6.70993L10.9992 9.47993L7.8792 6.70993C7.5492 6.41993 7.0492 6.42993 6.7292 6.72993C6.4292 7.04993 6.4192 7.53993 6.7092 7.86993L9.4792 10.9999L6.7092 14.1199C6.4192 14.4499 6.4192 14.9499 6.7292 15.2599C7.0492 15.5599 7.5392 15.5699 7.8692 15.2799L10.9992 12.5199L14.1192 15.2899C14.4492 15.5799 14.9492 15.5699 15.2592 15.2699C15.5592 14.9499 15.5692 14.4599 15.2792 14.1299L12.5192 10.9999L15.2892 7.87993C15.5792 7.54993 15.5792 7.04993 15.2692 6.73993C14.9492 6.42993 14.4492 6.41993 14.1192 6.70993ZM10.9992 11.0099L10.9892 10.9999L10.9992 10.9899L11.0092 10.9799L11.0192 10.9899L11.0292 10.9999L10.9992 11.0099Z"
													fill="white"
												></path>
											</g>
										</g>
									</svg>
									${this.errorMessage}
							  </p>`
							: null}
						<input
							${ref(this.codeInputRef)}
							id="responseCode"
							name="responseCode"
							aria-labelledby="responseCodeLabel"
							type="text"
							aria-errormessage="error-message"
							required
							minlength="12"
							maxlength="12"
							aria-required="true"
							aria-invalid="${this.errorMessage.length > 0}"
							class="w-full border-2 border-grijs-6 bg-grijs-2 p-1 text-center text-3xl font-bold uppercase leading-normal text-grijs-6"
						/>
						<button type="submit" class="btn btn--primary" aria-label="${textItems.getQuestionaire}">
							${textItems.getQuestionaire}
						</button>
					</form>
				</section>
			</dialog>
		`;
	}

	handleClose(event: Event): void {
		if (event instanceof KeyboardEvent && event.code !== 'Enter' && event.code !== 'Escape' && event.code !== 'Space') {
			return;
		}
		this.dialog.close();
	}
}

declare global {
	interface HTMLElementTagNameMap {
		'szw-ba-enter-code-dialog': SzwBaEnterCodeDialog;
	}
}
